import MemberCard from '../../components/TwoDays/MemberCard';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ReactComponent as DownloadIcon } from '@repo/common/assets/icons/16px/icon_16px_download.svg';
import { ReactComponent as BackIcon } from '@repo/common/assets/icons/16px/icon_16px_arrow-left.svg';
import {
  rejectReasonLabel,
  STATE_LABEL,
  acceptWaitMinutes,
  assigneesOf,
  chipsFor,
  cn,
  isAssigneeOf,
  isLeader,
  minutesLabel,
  overdueMinutes,
  presenceOf,
  waitingOnOf,
} from '../../components/TwoDays/model/derive';
import { KST, formatClock, formatDue, tzShortName } from '../../components/TwoDays/model/time';
import { dueInfo, useWorkboardCtx } from '../../components/TwoDays/model/useWorkboard';
import { StatusChip } from '../../components/TwoDays/primitives';
import { extOf, formatSize } from '../../components/TwoDays/fileUtils';
import Comments from '../../components/TwoDays/Comments';
import TdButton from '../../components/TwoDays/Button';
import AddTaskModal, { canEditTask } from '../../components/TwoDays/AddTaskModal';
import {
  AcceptModal,
  AddContributorModal,
  BlockerModal,
  ChangeReceiverModal,
  DeleteTaskModal,
  canDeleteTask,
  DeclineModal,
  ExpectedTimeModal,
  ReassignModal,
  RejectModal,
  ResetExpectedModal,
  ResolveBlockerModal,
  ReturnModal,
  ScopeChangeModal,
  SubmitModal,
} from '../../components/TwoDays/TaskActionModals';
import TaskListGroups, { LinkSlotList } from '../../components/TwoDays/TaskListGroups';
import SidePanel from '../../components/TwoDays/SidePanel';
import useInboxFocus, { litClass } from '../../components/TwoDays/useInboxFocus';
import { listOf, scopeRule } from '../../components/TwoDays/model/taskList';
import VersionLog, { ReviewGuide, useReviewGuide } from '../../components/TwoDays/VersionLog';
import TaskActivity from '../../components/TwoDays/TaskActivity';
import {
  MobileActionBar,
  MobileSummary,
  NextAction,
  RelayStrip,
  TaskInputs,
} from '../../components/TwoDays/TaskRelay';
import { activityOf, nextActionOf, versionsOf } from '../../components/TwoDays/model/relay';

const litBox = 'transition-[background-color,box-shadow] duration-300';
import Linkify from '../../components/TwoDays/Linkify';

const Section = ({ title, className, children, ...rest }) => (
  <section className={cn('flex flex-col gap-3', className)} {...rest}>
    <h2 className='text-[16px] font-medium leading-[150%] text-text-primary'>{title}</h2>
    {children}
  </section>
);

const SubLabel = ({ children }) => (
  <span className='text-[13px] leading-[140%] text-text-secondary'>{children}</span>
);

const cardClass = 'rounded-2xl border border-green-deep03/[0.06] bg-grayscale-gray00 shadow-4';
const pillChip = 'rounded-full px-[9px]';

/** 행동 버튼 — 2days 버튼(BaseButton): 주 행동(초록) · 보조(연한 초록) · 글자 버튼. 꺼져 있으면 tip 이 툴팁 */
const ActionButton = ({
  kind = 'text',
  onClick,
  disabled,
  describedBy,
  tip,
  highlight,
  children,
}) => (
  <TdButton
    variant={kind === 'primary' ? 'primary' : kind === 'secondary' ? 'soft' : 'ghost'}
    onClick={onClick}
    disabled={disabled}
    title={tip || undefined}
    aria-describedby={describedBy}
    className={cn(
      kind === 'text' && 'px-3 font-normal',
      highlight && 'ring-2 ring-green-deep02 ring-offset-1',
    )}
  >
    {children}
  </TdButton>
);

const FileRow = ({ file }) => (
  <li className='flex items-center gap-3 px-4 py-3'>
    <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-surface-green10 text-[9px] font-medium uppercase text-green-deep04'>
      {extOf(file.name)}
    </span>
    <span className='flex min-w-0 flex-1 flex-col'>
      <span className='truncate text-[14px] text-text-primary'>{file.name}</span>
      <span className='text-[12px] text-text-secondary'>
        {file.kind} · {typeof file.size === 'number' ? formatSize(file.size) : file.size}
      </span>
    </span>
    {file.url ? (
      // 실제 보드는 1시간짜리 받기 주소(원래 이름으로 내려받기) — 보드를 새로 받을 때마다 갱신된다
      <a
        href={file.url}
        download={file.name}
        target='_blank'
        rel='noreferrer'
        className='flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-surface-green10 px-3.5 text-[13px] font-medium text-text-primary hover:bg-green-light02/60'
      >
        <DownloadIcon className='h-4 w-4' /> Download
      </a>
    ) : (
      // 목 데이터의 예시 파일은 실제 파일이 없다
      <span
        title='Sample file — not downloadable in the demo'
        className='flex h-8 shrink-0 cursor-not-allowed items-center gap-1.5 rounded-full bg-surface-green10 px-3.5 text-[13px] font-medium text-text-placeholder'
      >
        <DownloadIcon className='h-4 w-4' /> Download
      </span>
    )}
  </li>
);

/** 오른쪽 요약 카드 한 줄 */
const InfoRow = ({ label, sub, children }) => (
  <div className='grid grid-cols-[112px_minmax(0,1fr)] gap-2 border-b border-green-deep03/[0.08] py-3 text-[14px] leading-[140%] last:border-b-0'>
    <span className='text-text-secondary'>{label}</span>
    <span className='flex min-w-0 flex-col gap-0.5 text-text-primary'>
      {children}
      {sub && <span className='text-[13px] leading-[140%] text-text-secondary'>{sub}</span>}
    </span>
  </div>
);

/** 상세 머리 안내 — info(연한 초록) · warn(노랑) */
const Alert = ({ tone, title, children }) => (
  <div
    role='status'
    className={cn(
      'flex gap-2.5 rounded-xl border px-4 py-3',
      tone === 'failed'
        ? 'border-functional-failed02 bg-functional-failed01'
        : tone === 'warn'
          ? 'border-functional-warning02 bg-functional-warning01'
          : 'border-green-deep03/[0.12] bg-surface-green10',
    )}
  >
    <span
      className={cn(
        'mt-2 h-1.5 w-1.5 shrink-0 rounded-full',
        tone === 'failed'
          ? 'bg-functional-failed03'
          : tone === 'warn'
            ? 'bg-functional-warning03'
            : 'bg-green-deep02',
      )}
    />
    <span className='flex min-w-0 flex-col gap-0.5'>
      <span
        className={cn(
          'text-[14px] font-medium leading-[150%]',
          tone === 'failed'
            ? 'text-functional-failed03'
            : tone === 'warn'
              ? 'text-functional-warning04'
              : 'text-text-primary',
        )}
      >
        {title}
      </span>
      <span className='text-[13px] leading-[150%] text-text-secondary'>{children}</span>
    </span>
  </div>
);

/**
 * 태스크 상세 — 전체 페이지는 릴레이 · 다음 행동 · Inputs · Work · Activity · 오른쪽 요약,
 * 보드 패널은 머리(칩 · 안내) · 내 행동 · 결과물과 인계 · 댓글 (Task Flows W2 · W3 · W5).
 * 행동은 보는 사람의 역할과 상태에 따라 달라진다:
 *   담당자 — Accept/Decline → Submit(앞 단계 승인 전에는 꺼짐) · Raise/Resolve blocker · 예상 시각 · 기여자
 *   받는 사람 — Task List 를 항목별 ✓ Pass / ✗ Fail → Approve(전부 ✓) / Reject(✗ 하나 이상) · 승인 뒤 Return to sender
 *   리더 — Reassign · Change scope (거절·무응답이면 Reassign 이 주 행동)
 * 프로젝트 시작 전(팀원은 팀 시작 전)에는 버튼이 꺼지고, 끝난 프로젝트·스태프 열람은 행동이 없다.
 */
export const TaskDetailBody = ({ taskId = '', inPanel = false }) => {
  const ctx = useWorkboardCtx();
  const location = useLocation();
  const navigate = useNavigate();
  const [modal, setModal] = useState(null);
  const [review, setReview] = useState({}); // 받는 사람 검토 — 'link:{이름}' · 'item:{항목}' → 'pass' | 'fail'
  const [detailsOpen, setDetailsOpen] = useState(false); // 태블릿 Details 시트
  const closeDetails = () => setDetailsOpen(false);
  // 기여자 등록 안내 — 담당자로 태스크 상세에 처음 들어왔을 때 한 번 (프로젝트 · 사람마다 기억)
  const tipKey = `2days-contributor-tip:${ctx.data.project.id}:${ctx.data.viewerId}`;
  const [tipSeen, setTipSeen] = useState(() => {
    try {
      return !!window.localStorage.getItem(tipKey);
    } catch {
      return false;
    }
  });
  // 수정 요청 안내 — 반려 사유 댓글마다 한 번 (Got it 으로 닫은 것)
  const [closedTips, setClosedTips] = useState([]);
  const dismissTip = () => {
    setTipSeen(true);
    try {
      window.localStorage.setItem(tipKey, '1');
    } catch {
      // 기억하지 못해도 이번에는 닫는다
    }
  };
  const guide = useReviewGuide(ctx.data.viewerId);
  const bannerRef = useRef(null);
  const task = ctx.taskById.get(taskId);
  const back = `${ctx.base}/tasks`;
  const backLabel = location.state?.from === 'mine' ? 'My tasks' : 'All tasks';
  const backTo = location.state?.from === 'mine' ? `${ctx.base}/tasks/mine` : back;

  // 담당자가 새 배정·바뀐 리스트를 처음 열면 표시를 지운다. 받는 사람이 검토할 결과물을 열면 열람으로 남는다
  const openSent = useRef(new Set());
  const viewerIsAssignee = !!task && isAssigneeOf(task, ctx.data.viewerId);
  const openKey = !task
    ? null
    : viewerIsAssignee && task.state === 'assigned' && !task.openedAt
      ? `${task.id}-a`
      : viewerIsAssignee && task.listChanged
        ? `${task.id}-list`
        : !ctx.readOnly && task.receiverId === ctx.data.viewerId && task.state === 'submitted'
          ? `${task.id}-r-${task.version}`
          : null;
  useEffect(() => {
    if (task && !task.listChanged) openSent.current.delete(`${task.id}-list`);
    if (!openKey || openSent.current.has(openKey)) return;
    openSent.current.add(openKey);
    ctx.dispatch({ type: 'openTask', taskId: task.id, at: ctx.now.toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey, task?.listChanged]);
  // 새 버전이 오면 검토 표시를 처음부터
  const versionKey = task ? `${task.id}-${task.version}-${task.state}` : '';
  useEffect(() => setReview({}), [versionKey]);

  const lit = useInboxFocus(!!task && !inPanel);
  useEffect(() => {
    if (lit?.focus !== 'accept' || !task || ctx.readOnly || ctx.actionsLocked) return;
    if (task.state === 'assigned' && isAssigneeOf(task, ctx.data.viewerId)) setModal('accept');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lit]);

  if (!task) {
    return (
      <div className='max-w-3xl'>
        <p className='text-[16px] font-medium text-text-primary'>Task not found</p>
        <Link to={back} className='mt-2 inline-block text-[14px] text-green-deep03 underline'>
          Back to all tasks
        </Link>
      </div>
    );
  }

  const { now, data, memberById } = ctx;
  const { adjusted, effective } = dueInfo(ctx, task);
  const tz = tzShortName(ctx.displayTz, now);
  const fmt = (d) => `${formatDue(d, ctx.displayTz)} ${tz}`;
  const viewerId = data.viewerId;
  const viewer = ctx.viewer;
  const leader = isLeader(viewer);
  const ended = data.project.status === 'ended';
  // 스태프 열람 · 끝난 프로젝트는 읽기 전용 — 행동을 보이지 않는다
  const readOnly = !!ctx.readOnly || ended;
  // 시작 전 — 리더는 프로젝트 시작부터, 팀원은 팀 시작(+1시간)부터 행동할 수 있다
  const projectStart = new Date(data.project.startAt);
  const teamStart = new Date(data.project.teamStartAt ?? projectStart.getTime() + 3_600_000);
  const opensAt = leader ? projectStart : teamStart;
  const locked = data.project.status === 'draft' || now < opensAt;
  const lockTip = `Available from ${formatClock(opensAt, KST)} KST`;

  const isAssignee = !readOnly && isAssigneeOf(task, viewerId);
  // 수정 요청을 받은 담당자 — 가장 최근 반려 · 되돌림 사유 댓글로 내리고 안내를 띄운다
  const reworkReason =
    isAssignee && task.state === 'rework_requested'
      ? (ctx.data.comments ?? [])
          .filter(
            (c) =>
              c.taskId === task.id && !c.parentId && ['rejection', 'return'].includes(c.type),
          )
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
      : null;
  const reworkKey = reworkReason ? `2days-rework-tip:${reworkReason.id}` : null;
  const reworkTipSeen =
    !reworkKey ||
    closedTips.includes(reworkKey) ||
    (() => {
      try {
        return !!window.localStorage.getItem(reworkKey);
      } catch {
        return false;
      }
    })();
  const dismissReworkTip = () => {
    setClosedTips((xs) => [...xs, reworkKey]);
    try {
      window.localStorage.setItem(reworkKey, '1');
    } catch {
      // 기억하지 못해도 이번에는 닫는다
    }
  };
  const isReceiver = !readOnly && task.receiverId === viewerId && !isAssignee;
  const working = task.state === 'in_progress' || task.state === 'rework_requested';
  const list = listOf(task, memberById);
  const items = list.full;
  const acceptor = !task.acceptedById || task.acceptedById === viewerId;
  // 착수는 유연하게, 제출은 엄격하게 — 앞 단계가 승인돼야 제출할 수 있다. 서버도 같은 규칙으로 막는다
  const waitingOn = waitingOnOf(task, ctx.taskById);
  // 공동 담당자 — 보는 사람이 담당자면 그 사람 근무 시간 기준
  const assignees = assigneesOf(task);
  const shared = assignees.length > 1;
  const wait = acceptWaitMinutes(
    task,
    now,
    memberById.get(isAssigneeOf(task, viewerId) ? viewerId : task.assigneeId),
  );
  const receiver = memberById.get(task.receiverId);
  const toNpc = receiver?.role === 'npc';
  const name = (id) => memberById.get(id)?.name ?? '–';
  const close = () => setModal(null);
  const comments = (data.comments ?? []).filter((c) => c.taskId === task.id);
  // 되돌림에 연결할 질문 — 내가 담당자에게 단, 해결되지 않은 질문
  const openQuestions = comments.filter(
    (c) =>
      c.type === 'question' &&
      !c.parentId &&
      !c.resolvedAt &&
      c.authorId === viewerId &&
      isAssigneeOf(task, c.targetUserId),
  );

  // ── 행동 ──
  const buttons = [];
  const P = (label, onClick, disabled, tip) =>
    buttons.push({ kind: 'primary', label, onClick, disabled, tip });
  const S = (label, onClick, disabled, tip) =>
    buttons.push({ kind: 'secondary', label, onClick, disabled, tip });
  const G = (label, onClick, highlight) => buttons.push({ kind: 'text', label, onClick, highlight });
  if (isAssignee && task.state === 'assigned') {
    P('Accept', () => setModal('accept'));
    S('Decline', () => setModal('decline'));
  }
  if (isAssignee && working) {
    P(
      task.state === 'rework_requested'
        ? 'Submit new version'
        : task.isFinal
          ? 'Submit final deliverable'
          : 'Submit deliverable',
      () => setModal('submit'),
      waitingOn.length > 0,
      waitingOn.length ? `Submit after “${waitingOn[0].title}” is approved` : '',
    );
    S(task.isBlocked ? 'Resolve blocker' : 'Raise blocker', () =>
      setModal(task.isBlocked ? 'resolve' : 'blocker'),
    );
    if (acceptor && task.expectedResetLeft)
      G('Reset expected submit time', () => setModal('reset'));
    if (acceptor) G('Change expected submit time', () => setModal('expected'));
    G(
      'Add contributor',
      () => {
        dismissTip();
        setModal('contributor');
      },
      !tipSeen,
    );
  }
  // 검수할 링크 슬롯 — 지금 버전의 슬롯 (테스크 리스트 위)
  const slotLinks = (task.deliverable?.slots ?? [])
    .filter((s) => s.url)
    .map((s) => ({ label: s.l, url: s.url }));
  const marks = [
    ...slotLinks.map((l) => [`link:${l.label}`, l.label]),
    ...items.map((label) => [`item:${label}`, label]),
  ];
  const allPass = marks.length > 0 && marks.every(([k]) => review[k] === 'pass');
  const failed = marks.filter(([k]) => review[k] === 'fail').map(([, label]) => label);
  const passed = marks.filter(([k]) => review[k] === 'pass').map(([, label]) => label);
  if (isReceiver && task.state === 'submitted') {
    P(
      'Approve',
      () => {
        ctx.dispatch({
          type: 'approve',
          taskId: task.id,
          items: marks.map(([, label]) => label),
          at: now.toISOString(),
        });
        // 앞 단계가 모두 끝나 제출이 열리는 다음 태스크
        const unlocked = data.tasks.filter(
          (t) =>
            (t.predecessorIds ?? []).includes(task.id) &&
            !['approved', 'cancelled'].includes(t.state) &&
            t.predecessorIds.every(
              (id) =>
                id === task.id || ['approved', 'cancelled'].includes(ctx.taskById.get(id)?.state),
            ),
        );
        ctx.toast?.(
          unlocked.length
            ? {
                title: 'Handoff approved',
                sub: `${[...new Set(unlocked.flatMap((t) => assigneesOf(t).map(name)))].join(', ')} can now submit ${unlocked
                  .map((t) => `“${t.title}”`)
                  .join(', ')}`,
              }
            : {
                title: 'Handoff approved',
                sub: `${assignees.map(name).join(', ')} get${shared ? '' : 's'} notified`,
              },
        );
      },
      !allPass,
      allPass ? '' : 'Mark every link slot and Task List item ✓',
    );
    S(
      'Reject',
      () => setModal('reject'),
      failed.length === 0,
      failed.length ? '' : 'Mark at least one item ✗ Fail',
    );
  }
  if (isReceiver && task.state === 'approved') {
    S(
      'Return to sender',
      () => setModal('return'),
      openQuestions.length === 0,
      openQuestions.length ? '' : 'Ask a question on this task first, then link it',
    );
  }
  // 거절된 태스크는 PM 이 받는 사람(또는 담당자였던 사람)이어도 다시 배정할 수 있다
  const leaderCanManage =
    !readOnly &&
    leader &&
    (task.state === 'declined' ||
      (!isAssigneeOf(task, viewerId) && task.receiverId !== viewerId)) &&
    ['assigned', 'declined', 'in_progress', 'rework_requested', 'submitted'].includes(task.state);
  const scope = scopeRule(task, now, effective, teamStart);
  const scopeButton = () =>
    S('Change scope', () => setModal('scope'), !scope.open, scope.open ? '' : scope.why);
  if (leaderCanManage) {
    if (task.state === 'declined' || wait >= 60) {
      P('Reassign', () => setModal('reassign'));
      if (task.state !== 'declined') scopeButton();
    } else {
      S('Reassign', () => setModal('reassign'));
      scopeButton();
    }
  } else if (
    !readOnly &&
    leader &&
    scope.free &&
    ['assigned', 'in_progress', 'rework_requested', 'submitted'].includes(task.state)
  ) {
    scopeButton();
  }
  if (
    !readOnly &&
    leader &&
    ['assigned', 'in_progress', 'submitted', 'rework_requested', 'declined'].includes(task.state)
  ) {
    G('Change receiver', () => setModal('receiver'));
  }
  // PM 이 만든 태스크 — 받는 사람이 승인하기 전이면 고칠 수 있다
  if (!readOnly && leader && canEditTask(task)) {
    G('Edit task', () => setModal('edit'));
  }
  // PM 이 만든 태스크 · 거절된 태스크 — 한 번도 제출되지 않았으면 지울 수 있다
  if (!readOnly && leader && canDeleteTask(task)) {
    G('Delete task', () => setModal('delete'));
  }

  // ── 머리 안내 ──
  let alert = null;
  const late = overdueMinutes(task, ctx);
  if (!readOnly) {
    if (late > 0 && isAssigneeOf(task, viewerId)) {
      // 마감이 지났는데 제출 전 — 가장 먼저 알린다
      alert = [
        'failed',
        `Overdue · ${minutesLabel(late)} past the due time`,
        task.state === 'assigned'
          ? 'Accept and submit as soon as you can, or decline so your leader can reassign it. Your leader and ops have been notified.'
          : 'Submit as soon as you can and tell your teammates where you are in a comment. A late submission already lowers your score, and your leader and ops have been notified.',
      ];
    } else if (late > 0 && leader) {
      alert = [
        'failed',
        `Overdue · ${minutesLabel(late)} past the due time`,
        'Check in with the assignee in a comment, change the scope, or reassign it. Ops has been notified.',
      ];
    } else if (late > 0) {
      alert = [
        'warn',
        `Overdue · ${minutesLabel(late)}`,
        'This task is past its due time. Work that depends on it may be delayed.',
      ];
    } else if (isAssigneeOf(task, viewerId) && wait >= 30) {
      alert = [
        'warn',
        wait >= 60
          ? `No response · ${minutesLabel(wait)}`
          : `Accept pending · ${minutesLabel(wait)}`,
        wait >= 60
          ? 'Accept or decline now. Your leader can reassign this task.'
          : 'Accept or decline within 60 min of assignment. After that your leader can reassign it.',
      ];
    } else if (leader && !isAssigneeOf(task, viewerId) && wait >= 60) {
      alert = [
        'warn',
        `No response · ${minutesLabel(wait)}`,
        'Reassign it with the reason “No response to acceptance”. Ops is called at 60 min.',
      ];
    } else if (isAssigneeOf(task, viewerId) && working && acceptor && task.expectedResetLeft) {
      alert = [
        'info',
        'The Task List changed after you accepted',
        'Check the updated list. You can reset your expected submit time once, and it doesn’t count as a delay notice.',
      ];
    } else if (isAssigneeOf(task, viewerId) && working && waitingOn.length) {
      alert = [
        'info',
        'Submit opens after earlier tasks are approved',
        `Waiting on ${waitingOn.map((p) => `“${p.title}” (${STATE_LABEL[p.state]})`).join(', ')}. You can keep working.`,
      ];
    } else if (isAssigneeOf(task, viewerId) && toNpc && ['submitted', 'approved'].includes(task.state)) {
      alert = [
        'info',
        'Sent to team lead Kim · Done',
        task.isFinal
          ? 'Peer review is open for your team. The rubric comes from the team lead.'
          : 'The team lead may send it back with notes.',
      ];
    } else if (isAssigneeOf(task, viewerId) && task.isFinal && task.state === 'rework_requested') {
      alert = [
        'warn',
        'The team lead asked for changes',
        'Submit a new version. Your team’s peer review deadline restarts when you resubmit.',
      ];
    }
  }

  // ── 버전 · Task List 모드 ──
  const versions = versionsOf(task);
  const latest = versions[0];
  let mode = 'plain';
  let results = {};
  if (isReceiver && task.state === 'submitted' && !locked) {
    mode = 'review';
    results = review;
  } else if (latest) {
    if (task.state === 'submitted') mode = 'sender';
    else if (latest.review) {
      mode = 'ro';
      const failedSet = latest.review.failedItems ?? [];
      const passedSet = latest.review.passedItems;
      if (!latest.review.returned) {
        results = Object.fromEntries(
          marks.map(([k, label]) => [
            k,
            latest.review.result === 'approved'
              ? 'pass'
              : failedSet.includes(label)
                ? 'fail'
                : !passedSet || passedSet.includes(label)
                  ? 'pass'
                  : null,
          ]),
        );
      }
    }
  }
  // 반려 사유 댓글이 없는 반려(예: 예전 기록 · 팀장) — 상자로 보여 준다
  const hasReasonComment = comments.some((c) => c.type === 'rejection' || c.type === 'return');

  // ── 오른쪽 요약 ──
  // 담당자 · 받는 사람 — 접속 · 작업 시간 상태는 보이지 않는다. 이탈한 사람만 Dropped
  const personSub = (id) => {
    const m = memberById.get(id);
    if (!m || m.role === 'npc') return null;
    return presenceOf(m, now) === 'dropped' ? 'Dropped' : null;
  };
  const predecessors = (task.predecessorIds ?? [])
    .map((id) => ctx.taskById.get(id))
    .filter(Boolean);

  // 오른쪽 요약 — 패널에서는 머리 바로 아래 (Task Flows 1a)
  const info = (
    <aside className={cn(cardClass, 'px-4 py-1')}>
      {/* 이름을 누르면 팀원 카드 (읽기 전용) */}
      {shared ? (
        // 공동 담당자 — 누구든 수락·제출할 수 있다
        <InfoRow label='Assignees' sub='Any assignee can accept and submit'>
          <span className='flex flex-wrap gap-x-1'>
            {assignees.map((id, k) => (
              <span key={id} className='inline-flex'>
                <MemberCard memberId={id} />
                {k < assignees.length - 1 && ','}
              </span>
            ))}
          </span>
        </InfoRow>
      ) : (
        <InfoRow label='Assignee' sub={personSub(task.assigneeId)}>
          <MemberCard memberId={task.assigneeId} />
        </InfoRow>
      )}
      <InfoRow label='Receiver' sub={personSub(task.receiverId)}>
        <MemberCard memberId={task.receiverId} />
      </InfoRow>
      {predecessors.length > 0 && (
        <InfoRow
          label='Hand off after'
          sub={waitingOn.length ? 'Submit opens when these are approved' : 'All approved'}
        >
          {predecessors.map((p) => p.title).join(', ')}
        </InfoRow>
      )}
      {task.plannedStartAt && <InfoRow label='Planned start'>{fmt(task.plannedStartAt)}</InfoRow>}
      {adjusted ? (
        <InfoRow label='Adjusted due' sub={`Original ${fmt(task.dueAt)}`}>
          {fmt(adjusted)}
        </InfoRow>
      ) : (
        <InfoRow label='Due'>{fmt(task.dueAt)}</InfoRow>
      )}
      {task.expectedSubmitAt && (
        <InfoRow label='Expected submit'>{fmt(task.expectedSubmitAt)}</InfoRow>
      )}
      {(leader || ctx.readOnly) && task.skillTags?.length > 0 && (
        <InfoRow label='Skill tags' sub={task.code ? 'From the brief task' : null}>
          {task.skillTags.join(', ')}
        </InfoRow>
      )}
      {/* 기여자 — 추가하면 카드 맨 아래에 이름과 사유가 쌓인다 */}
      {task.contributors?.length > 0 && (
        <InfoRow label='Contributors'>
          <ul className='flex flex-col gap-2'>
            {task.contributors.map((c, i) => (
              <li key={`${c.userId}-${i}`} className='flex flex-col'>
                <span>{name(c.userId)}</span>
                <span className='text-[12px] leading-[140%] text-text-secondary'>{c.reason}</span>
              </li>
            ))}
          </ul>
        </InfoRow>
      )}
    </aside>
  );

  const short = (d) => (d ? formatDue(d, ctx.displayTz) : '–');
  const nexts = data.tasks.filter(
    (t) => (t.predecessorIds ?? []).includes(task.id) && t.state !== 'cancelled',
  );
  const taskPath = (id) => `${ctx.base}/tasks/${id}`;
  const showGuide = mode === 'review' && !guide.seen;
  const slotLabels = slotLinks.map((l) => l.label);
  const primary = buttons.find((b) => b.kind === 'primary');
  const actionsLit = ['accept', 'actions'].includes(lit?.focus);
  const pick = (k, v) => setReview((r) => ({ ...r, [k]: v }));

  const work = (
    <>
      {inPanel && (
        <div className='flex flex-col gap-1.5'>
          <SubLabel>Files{task.files?.length ? ` (${task.files.length})` : ''}</SubLabel>
          {task.files?.length ? (
            <ul className={cn(cardClass, 'divide-y divide-green-deep03/[0.08] overflow-hidden')}>
              {task.files.map((f) => (
                <FileRow key={f.name} file={f} />
              ))}
            </ul>
          ) : (
            <span className='text-[14px] leading-[150%] text-text-tertiary'>No files attached</span>
          )}
        </div>
      )}
      <VersionLog task={task} items={items} viewerId={viewerId} name={name} fmt={short} />
      {showGuide && <ReviewGuide onDismiss={guide.dismiss} />}
      <div
        data-inbox-lit={lit?.focus === 'review' || undefined}
        className={cn(
          '-m-3 flex flex-col gap-4 rounded-2xl p-3',
          litBox,
          litClass(lit?.focus === 'review'),
        )}
      >
        <LinkSlotList
          links={mode === 'review' || mode === 'ro' ? slotLinks : null}
          mode={mode}
          results={results}
          onPick={pick}
        />
        <div className='flex flex-col gap-1.5'>
          <SubLabel>Task List</SubLabel>
          <TaskListGroups
            type={list.type}
            base={list.base}
            items={list.items}
            sources={list.sources}
            mode={mode}
            results={results}
            onPick={pick}
          />
        </div>
        {mode === 'review' && (
          <span className='text-[12px] text-text-secondary'>
            Mark every link slot and item. Approve needs all ✓ Pass · Reject needs at least one ✗
            Fail. If a link won’t open, mark it ✗ and pick “Can’t open the link or no access”.
          </span>
        )}
      </div>
      {task.review?.result === 'rejected' && task.state === 'rework_requested' && !hasReasonComment && (
        <div className='flex flex-col gap-1 rounded-xl bg-functional-failed01 px-4 py-3'>
          <span className='text-[13px] font-medium text-functional-failed04'>
            {task.review.returned ? 'Returned' : 'Rejected'}
            {task.review.reason ? ` · ${rejectReasonLabel(task.review.reason) ?? ''}` : ''}
          </span>
          <span className='whitespace-pre-line text-[14px] text-text-primary'>
            <Linkify text={task.review.description} />
          </span>
        </div>
      )}
    </>
  );

  const chips = (
    <div className='flex flex-wrap gap-1.5'>
      {chipsFor(task, viewerId, ctx)
        .filter((c) => c.label !== 'New request')
        .map((c) => (
          <StatusChip key={c.label} chip={c} className={pillChip} />
        ))}
      {toNpc && ['submitted', 'approved'].includes(task.state) && (
        <StatusChip chip={{ label: 'Sent to team lead Kim', tone: 'gray' }} className={pillChip} />
      )}
    </div>
  );
  const declineNote = task.state === 'declined' && task.declineReason && (
    <div className='flex flex-col gap-0.5 text-[14px] leading-[150%]'>
      <span className='text-text-secondary'>Decline reason</span>
      <span className='text-text-tertiary'>
        <Linkify text={task.declineReason} />
      </span>
    </div>
  );
  const modals = (
    <>
      {modal === 'accept' && <AcceptModal task={task} onClose={close} />}
      {modal === 'reject' && (
        <RejectModal
          task={task}
          failedItems={failed}
          passedItems={passed}
          slotLabels={slotLabels}
          onClose={close}
        />
      )}
      {modal === 'return' && <ReturnModal task={task} questions={openQuestions} onClose={close} />}
      {modal === 'decline' && <DeclineModal task={task} onClose={close} />}
      {modal === 'submit' && <SubmitModal task={task} onClose={close} />}
      {modal === 'blocker' && <BlockerModal task={task} onClose={close} />}
      {modal === 'resolve' && <ResolveBlockerModal task={task} onClose={close} />}
      {modal === 'expected' && <ExpectedTimeModal task={task} onClose={close} />}
      {modal === 'contributor' && <AddContributorModal task={task} onClose={close} />}
      {modal === 'reassign' && <ReassignModal task={task} onClose={close} />}
      {modal === 'scope' && <ScopeChangeModal task={task} onClose={close} />}
      {modal === 'receiver' && <ChangeReceiverModal task={task} onClose={close} />}
      {modal === 'delete' && (
        <DeleteTaskModal task={task} onClose={close} onDeleted={() => navigate(backTo)} />
      )}
      {modal === 'reset' && <ResetExpectedModal task={task} onClose={close} />}
      {modal === 'edit' && <AddTaskModal task={task} onClose={close} />}
    </>
  );
  // 기여자 등록 안내 — 처음 한 번. 도움받은 기록은 양쪽 Growth Report 에 남는다
  const contributorTip = buttons.some((b) => b.highlight) && !locked && (
    <div
      id='contributor-tip'
      role='note'
      className='flex flex-wrap items-start gap-x-4 gap-y-2 rounded-xl bg-grayscale-gray09 px-4 py-3 text-[13px] leading-[150%] text-grayscale-gray00 shadow-popup-01'
    >
      <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <span className='font-medium'>Did a teammate help you?</span>
        <span className='text-grayscale-gray03'>
          Use Add contributor whenever someone answers a question, unblocks you or reviews your
          work. It’s recorded on this task and shows in both of your Growth Reports.
        </span>
      </span>
      <button
        type='button'
        onClick={dismissTip}
        className='h-8 shrink-0 rounded-lg bg-grayscale-gray00/15 px-3 text-[13px] font-medium hover:bg-grayscale-gray00/25'
      >
        Got it
      </button>
    </div>
  );
  const commentGuide = {
    reworkId: reworkReason?.id ?? null,
    onGuideDismiss: reworkReason && !reworkTipSeen ? dismissReworkTip : null,
  };

  if (inPanel) {
    return (
      <div className='grid items-start gap-x-10 gap-y-8'>
        <div className='flex min-w-0 flex-col gap-8'>
          <div className='flex flex-col gap-3'>
            <div className='flex flex-col gap-3'>
              <h1 className='m-0 text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
                {task.title}
              </h1>
              {chips}
            </div>
            {alert && (
              <Alert tone={alert[0]} title={alert[1]}>
                {alert[2]}
              </Alert>
            )}
            {declineNote}
          </div>

          {info}

          <div className='flex flex-col gap-10'>
            {buttons.length > 0 && (
              <Section
                title='Actions'
                data-inbox-lit={actionsLit || undefined}
                className={cn('-m-3 rounded-xl p-3', litBox, litClass(actionsLit))}
              >
                <div className='flex flex-wrap items-center gap-2'>
                  {buttons.map((b) => (
                    <ActionButton
                      key={b.label}
                      kind={b.kind}
                      onClick={b.onClick}
                      disabled={b.disabled || locked}
                      tip={locked ? lockTip : b.tip}
                      highlight={b.highlight && !locked}
                      describedBy={b.highlight && !locked ? 'contributor-tip' : undefined}
                    >
                      {b.label}
                    </ActionButton>
                  ))}
                </div>
                {contributorTip}
                {locked && <p className='text-[13px] text-text-secondary'>{lockTip}</p>}
              </Section>
            )}

            <Section title='Deliverable and handoff' className='gap-4'>
              {work}
            </Section>

            <Comments
              task={task}
              focusId={lit?.focus === 'comment' ? lit.commentId : null}
              {...commentGuide}
            />
          </div>
        </div>
        {modals}
      </div>
    );
  }

  const leaderMember = [...memberById.values()].find(isLeader);
  const banner = nextActionOf({
    task,
    isA: isAssignee,
    isR: isReceiver,
    leader: leader && !readOnly,
    ended,
    preLock: locked && !readOnly,
    lockTip,
    alert,
    gate: waitingOn,
    next: nexts,
    name,
    fmt: short,
    comments,
    expected: task.expectedSubmitAt ?? effective,
    due: effective,
  });
  const events = activityOf({
    task,
    comments,
    name,
    leaderName: leaderMember?.name ?? 'PM',
    viewerId,
    seeExpected: leader || !!ctx.readOnly,
    fmt: short,
  });
  const summaryRows = [
    [shared ? 'Assignees' : 'Assignee', assignees.map(name).join(', ')],
    ['Receiver', name(task.receiverId)],
    ...(predecessors.length
      ? [['Hand off after', predecessors.map((p) => p.title).join(', ')]]
      : []),
    adjusted ? ['Adjusted due', fmt(adjusted)] : ['Due', fmt(task.dueAt)],
    ...(task.expectedSubmitAt ? [['Expected submit', fmt(task.expectedSubmitAt)]] : []),
    ...(task.contributors?.length
      ? [['Contributors', task.contributors.map((c) => name(c.userId)).join(', ')]]
      : []),
  ];

  return (
    <div
      className={cn(
        'grid items-start gap-x-10 gap-y-8 tabletV:grid-cols-[minmax(0,1fr)_320px]',
        primary && 'pb-[88px] tabletH:pb-0',
      )}
    >
      <div className='flex min-w-0 flex-col gap-8'>
        <header className='flex flex-col gap-3'>
          {/* 글자 화살표(←)는 Safari 에서 안 보일 때가 있어 아이콘으로 쓴다 */}
          <Link
            to={backTo}
            className='inline-flex w-fit items-center gap-1 text-[13px] text-text-secondary hover:text-text-primary'
          >
            <BackIcon aria-hidden className='h-4 w-4 shrink-0' />
            {backLabel}
          </Link>
          <div className='flex items-start justify-between gap-3'>
            <h1 className='m-0 text-[28px] font-medium leading-[120%] tracking-[-0.01em] text-text-primary'>
              {task.title}
            </h1>
            <button
              type='button'
              onClick={() => setDetailsOpen(true)}
              className='hidden h-9 shrink-0 rounded-full bg-surface-green10 px-4 text-[14px] font-medium text-text-primary tabletH:inline-flex tabletH:items-center tabletV:hidden'
            >
              Details
            </button>
          </div>
          {chips}
          <RelayStrip
            task={task}
            prevs={predecessors}
            nexts={nexts}
            name={name}
            fmt={short}
            to={taskPath}
          />
          <MobileSummary rows={summaryRows} />
          {declineNote}
        </header>

        <div className='flex flex-col gap-10'>
          <NextAction
            innerRef={bannerRef}
            banner={banner}
            buttons={buttons}
            locked={locked}
            lockTip={lockTip}
            data-inbox-lit={actionsLit || undefined}
            className={cn(
              'transition-shadow duration-300',
              actionsLit && 'shadow-[0_0_0_3px_rgba(29,195,123,0.35)]',
            )}
          >
            {contributorTip}
          </NextAction>
          <TaskInputs
            task={task}
            prevs={predecessors}
            ctx={ctx}
            name={name}
            fmt={short}
            to={taskPath}
          />
          <section className='flex flex-col gap-4'>
            <div className='flex flex-col gap-0.5'>
              <h2 className='text-[16px] font-medium leading-[150%] text-text-primary'>Work</h2>
              <span className='text-[13px] leading-[140%] text-text-secondary'>
                Submissions and Task List · review results sit with each version
              </span>
            </div>
            {work}
          </section>
          <TaskActivity
            task={task}
            events={events}
            focusId={lit?.focus === 'comment' ? lit.commentId : null}
            {...commentGuide}
          />
        </div>
      </div>

      <div className='hidden tabletV:block'>{info}</div>
      <SidePanel
        open={detailsOpen}
        onClose={closeDetails}
        label='Details'
        width='max-w-[400px]'
        z='z-[900]'
        className='gap-4 overflow-y-auto bg-bg-twodays p-5'
      >
        <div className='flex items-center justify-between'>
          <span className='text-[16px] font-medium text-text-primary'>Details</span>
          <button
            type='button'
            onClick={closeDetails}
            className='h-9 rounded-full bg-surface-green10 px-4 text-[14px] font-medium text-text-primary'
          >
            Close
          </button>
        </div>
        {info}
      </SidePanel>
      <MobileActionBar
        title={banner.title}
        primary={primary}
        more={buttons.length > 1}
        onMore={() => bannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
        locked={locked}
        lockTip={lockTip}
      />
      {modals}
    </div>
  );
};

const TwoDaysTaskDetail = () => {
  const { taskId = '' } = useParams();
  return <TaskDetailBody taskId={taskId} />;
};

export default TwoDaysTaskDetail;
